import React, { useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import ClientFirstDocumentEditor from '@/features/dms/EdgeDocs/ClientFirst/ClientFirstDocumentEditor';
import { DocumentAPI } from '@/features/dms/DocumentAPI';

export const ClientFirstDocumentEditorPage: React.FC = () => {
  console.log('🔥 [ClientFirstDocumentEditorPage] Component is rendering!');
  
  const { documentId } = useParams<{ documentId: string }>();
  const [searchParams] = useSearchParams();
  const { keycloak } = useKeycloak();
  const { useDocumentAPI } = DocumentAPI;
  const { trackDocumentAccess } = useDocumentAPI();
  const sessionIdRef = useRef<string>('');
  
  // Get versionId from URL params if editing a specific version
  const versionId = searchParams.get('versionId');
  
  // CRITICAL DEBUG: Track URL parameter extraction
  console.log('🔥 [ClientFirstDocumentEditorPage] URL DEBUG:', {
    fullURL: window.location.href,
    search: window.location.search,
    searchParams: Object.fromEntries(searchParams.entries()),
    documentId,
    versionId,
    versionIdRaw: searchParams.get('versionId'),
    timestamp: new Date().toISOString()
  });
  console.log('🔥 [ClientFirstDocumentEditorPage] Keycloak authenticated:', keycloak?.authenticated);

  useEffect(() => {
    if (!documentId || !keycloak?.authenticated) return;
    if (!sessionIdRef.current) {
      const versionLabel = versionId || 'latest';
      sessionIdRef.current = `dms-view-${documentId}-${versionLabel}-${Date.now()}`;
    }
    trackDocumentAccess(documentId, 'view', sessionIdRef.current).catch((error) => {
      console.warn('[ClientFirstDocumentEditorPage] Failed to track document access:', error);
    });
  }, [documentId, versionId, keycloak?.authenticated, trackDocumentAccess]);

  // Convert HTTP(S) to WebSocket URL properly
  const websocketUrl = window.location.origin
    .replace(/^https:/, 'wss:')
    .replace(/^http:/, 'ws:') + '/collaboration';
  const token = keycloak?.token || '';
  const realmId = (keycloak as any)?.realm || '';

  // DMS Configuration for backend typesetter integration
  const dmsBaseUrl = window.location.origin; // Same server hosts both frontend and backend
  const authToken = token; // Use the same Keycloak token for DMS API calls

  console.log('🚀 [ClientFirstDocumentEditorPage] DMS Config:', { dmsBaseUrl, authToken: !!authToken });

  // Attorney info from JWT token
  const attorney: any = {
    id: keycloak?.tokenParsed?.sub || 'unknown',
    name: keycloak?.tokenParsed?.name || 'Unknown Attorney',
    email: keycloak?.tokenParsed?.email || 'unknown@example.com',
    title: 'Attorney',
    firm: 'EdgeLex',
    address: '',
    phone: '',
    jurisdiction: 'federal' as const,
    role: 'attorney' as const,
    seniority: 'senior' as const,
    firmName: realmId.replace('-realm', '')
  };

  const auth = {
    token,
    realmId
  };

  if (!documentId) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600 dark:text-gray-300">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Missing Document ID</h2>
          <p>Unable to load document - document ID not found in route.</p>
        </div>
      </div>
    );
  }

  if (!keycloak?.authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-600 dark:text-gray-300">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Authentication Required</h2>
          <p>Please log in to access the document editor.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="h-screen flex flex-col">
        {/* Header with document info */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                EdgeDocs Client-First Editor
              </h1>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Document: {documentId.substring(0, 8)}...
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-green-600 dark:text-green-400">
                ● Connected
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {attorney.name}
              </span>
            </div>
          </div>
        </div>

        {/* Client-First Editor */}
        <div className="flex-1 overflow-hidden">
          {(() => {
            console.log('🔥 [ClientFirstDocumentEditorPage] About to render ClientFirstDocumentEditor with:', {
              documentId,
              attorney: attorney.name,
              websocketUrl,
              authToken: !!auth.token
            });
            return null;
          })()}
          <ClientFirstDocumentEditor
            documentId={documentId}
            versionId={versionId || undefined}
            attorney={attorney}
            websocketUrl={websocketUrl}
            auth={auth}
            jurisdiction="federal"
            documentType="contract"
            dmsBaseUrl={dmsBaseUrl}
            authToken={authToken}
            enableAutoSave={true}
            autoSaveInterval={30000}
            onDocumentChange={(state) => {
              console.log('Document changed:', {
                textLength: state.text.length,
                caretPosition: state.caretPosition,
                hasSelection: !!state.selection
              });
            }}
            onCollaboratorJoin={(collaborator) => {
              console.log('Collaborator joined:', collaborator);
            }}
            onError={(error) => {
              console.error('Client-First Editor Error:', error);
            }}
            onDocumentSaved={(docId) => {
              console.log('Document saved:', docId);
            }}
            onVersionCreated={(versionId) => {
              console.log('Version created:', versionId);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientFirstDocumentEditorPage;
